import { type CSSProperties, type FC } from 'react'
import ReactECharts from 'echarts-for-react'
import { twMerge } from 'tailwind-merge'
import cx from 'classnames'
import { type DefaultProps } from '../common'
import { usePieChartData } from './use-pie-chart-data'
import { createChartOptions } from './create-options'

export interface PieChartProps extends DefaultProps {
  segmentOne: number
  segmentTwo: number
  segmentThree: number
  segmentFour?: number
  highlightedSegment: number
  showAdditionalOverlays?: boolean
  showPercentage?: boolean
  highlightAllSegments?: boolean
  style?: CSSProperties | undefined
  fontSize?: number
}

export const PieChart: FC<PieChartProps> = ({
  segmentOne,
  segmentTwo,
  segmentThree,
  segmentFour,
  highlightedSegment,
  showAdditionalOverlays = false,
  showPercentage = true,
  highlightAllSegments = false,
  className,
  id,
  testId,
  ariaLabel,
  style,
  fontSize = 26,
}) => {
  const limitedSegment = Math.max(1, Math.min(highlightedSegment, segmentFour ? 4 : 3))

  const { createSegment } = usePieChartData({
    segmentOne,
    segmentTwo,
    segmentThree,
    segmentFour,
    highlightedSegment: limitedSegment,
    showAdditionalOverlays,
    highlightAllSegments,
  })

  const option = createChartOptions({
    segmentOne,
    segmentTwo,
    segmentThree,
    segmentFour,
    highlightedSegment: limitedSegment,
    showPercentage,
    createSegment,
    fontSize,
  })

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('aspect-w-1 flex min-w-20')
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      aria-hidden="true"
    >
      <ReactECharts className="w-full flex-1 !h-auto [&>div]:!cursor-default" style={style} option={option} notMerge />
    </div>
  )
}
